import { utcMonday, utcMonth } from "d3-time";

// Pure helpers + type for the page-level granularity toggle. Split out
// from GranularityControl.tsx so React Fast Refresh can do its job (the
// .tsx file is now component-only).

export type Granularity = "day" | "week" | "month";

/**
 * Smart default for the granularity toggle, derived from the date-range
 * window in days. Lifted from the M12.5 TimeSeriesSection unchanged so
 * we don't drift from the granularity shape that ships with M12. The
 * caller passes the inclusive ISO bounds; we count the days inclusive
 * of both ends.
 */
export function inferGranularity(startDate: string, endDate: string): Granularity {
  const startMs = new Date(`${startDate}T00:00:00Z`).getTime();
  const endMs = new Date(`${endDate}T00:00:00Z`).getTime();
  const days = Math.max(1, Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1);
  if (days <= 30) return "day";
  if (days <= 120) return "week";
  return "month";
}

/** Maps an ISO day to its day / week / month bucket key. Buckets are UTC
 *  (the local-time floors put Sunday labels on Monday weeks east of UTC),
 *  and a bucket that starts before the first day in the data is keyed by
 *  that first day, so no row is labelled outside the report's range. */
export function makeDateBucketizer(
  granularity: Granularity,
  firstDate: string,
): (iso: string) => string {
  if (granularity === "day") return (iso) => iso;
  const floor = granularity === "week" ? utcMonday : utcMonth;
  return (iso) => {
    const key = floor
      .floor(new Date(`${iso}T00:00:00Z`))
      .toISOString()
      .slice(0, 10);
    return key < firstDate ? firstDate : key;
  };
}

/** Earliest ISO date in a list of rows. */
export function firstIsoDate(rows: ReadonlyArray<{ date: string }>): string {
  let first = rows[0]?.date ?? "";
  for (const r of rows) if (r.date < first) first = r.date;
  return first;
}
